<template>
  <div class="flex h-screen" data-testid="affiliate-layout">
    <AffiliateSidebar
      class="hidden lg:flex"
      :me="store.me"
      :current-page="page"
      :open-count="store.openPackageCount"
      :portal-path="portalPath"
    />
    <div class="flex flex-1 flex-col overflow-hidden">
      <EnvironmentStrip />
      <AffiliateImpersonationBanner
        v-if="scope.kind === 'admin'"
        :affiliate-name="store.me?.affiliate.name ?? 'este afiliado'"
        @back="router.push(PATHS.ANTECIPE_AFFILIATES)"
      />
      <nav
        class="flex gap-2 overflow-x-auto border-b border-gray-200 px-4 py-2.5 lg:hidden"
        aria-label="Menu do portal"
        data-testid="affiliate-mobile-nav"
      >
        <router-link
          v-for="item in AFFILIATE_NAV"
          :key="item.page"
          :to="portalPath(item.page)"
          class="whitespace-nowrap rounded-full border px-3 py-1 text-[13px]"
          :class="
            item.page === page
              ? 'border-primary bg-primary text-white'
              : 'border-lightGray30 bg-white'
          "
        >
          {{ item.label }}
        </router-link>
      </nav>
      <AffiliateHeader
        :title="titles.title"
        :subtitle="subtitle"
        :show-breadcrumb="page !== 'overview'"
        :home-path="portalPath('overview')"
        :initials="initials"
        :can-logout="scope.kind === 'self'"
        @logout="authStore.logout()"
      />
      <div class="flex flex-1 flex-col overflow-y-auto">
        <main class="flex-1 bg-white px-4 pb-8 pt-2 lg:px-8">
          <div
            v-if="store.meError"
            class="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-left text-[13px] text-red-700"
            data-testid="affiliate-layout-error"
          >
            {{ store.meError }}
            <button
              type="button"
              class="rounded border border-red-200 bg-white px-3 py-1 text-xs font-medium"
              @click="store.loadMe(true)"
            >
              Tentar novamente
            </button>
          </div>
          <div
            v-else-if="!store.me"
            class="h-40 animate-pulse rounded-lg bg-lightGray30"
            data-testid="affiliate-layout-loading"
          />
          <slot v-else />
        </main>
        <Footer />
      </div>
    </div>
    <AffiliatePackageDrawer />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import EnvironmentStrip from '@/components/EnvironmentStrip.vue'
import Footer from '@/components/Footer.vue'
import AffiliateHeader from '@/modules/affiliate/components/AffiliateHeader.vue'
import AffiliateImpersonationBanner from '@/modules/affiliate/components/AffiliateImpersonationBanner.vue'
import AffiliatePackageDrawer from '@/modules/affiliate/components/AffiliatePackageDrawer.vue'
import AffiliateSidebar from '@/modules/affiliate/components/AffiliateSidebar.vue'
import { initialsOf } from '@/modules/affiliate/format'
import { AFFILIATE_NAV, AFFILIATE_PAGE_TITLES } from '@/modules/affiliate/labels'
import type { AffiliatePage } from '@/modules/affiliate/labels'
import { useAffiliatePortalNav } from '@/modules/affiliate/useAffiliateScope'
import { useAntecipeAffiliateStore } from '@/pinia-store/useAntecipeAffiliateStore'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import PATHS from '@/router/paths'

const route = useRoute()
const router = useRouter()
const store = useAntecipeAffiliateStore()
const authStore = useAuthStore()
const { scope, portalPath } = useAffiliatePortalNav()

const page = computed<AffiliatePage>(() => route.meta.affiliatePage ?? 'overview')
const titles = computed(() => AFFILIATE_PAGE_TITLES[page.value])
const subtitle = computed(() =>
  store.me
    ? titles.value.subtitle({ name: store.me.affiliate.name, slaHours: store.me.slaHours })
    : ''
)
const initials = computed(() =>
  scope.value.kind === 'admin' ? initialsOf(authStore.getUserName) : (store.me?.user.initials ?? '')
)

// Escopo antes de qualquer página montar (o slot só aparece depois do /me).
watch(
  scope,
  (next) => {
    store.setScope(next)
    void store.loadMe()
  },
  { immediate: true, deep: true }
)
</script>
